import { supabase } from './supabase';
import { functionsFetch } from './functionsClient';

/**
 * Normalize a name for comparison (lowercase, trim, remove extra spaces)
 */
function normalizeName(name: string): string {
  return name.toLowerCase().trim().replace(/\s+/g, ' ');
}

/**
 * Extract initials from a name
 * Example: "Theodora M. Scarato" -> ["T", "M", "S"]
 */
function extractInitials(name: string): string[] {
  const parts = name.split(/\s+/);
  return parts
    .map(part => {
      // Remove punctuation and get first character
      const cleaned = part.replace(/[^a-zA-Z]/g, '');
      return cleaned.length > 0 ? cleaned[0].toUpperCase() : null;
    })
    .filter((initial): initial is string => initial !== null);
}

/**
 * Check if two names match using fuzzy matching
 * Handles variations like:
 * - "Theodora M. Scarato" vs "T. Scarato"
 * - "Theodora M. Scarato" vs "Theodora Scarato"
 * - "T. M. Scarato" vs "Theodora M. Scarato"
 */
export function fuzzyMatchName(name1: string, name2: string): boolean {
  const normalized1 = normalizeName(name1);
  const normalized2 = normalizeName(name2);

  // Exact match after normalization
  if (normalized1 === normalized2) {
    return true;
  }

  // Split into parts
  const parts1 = normalized1.split(/\s+/);
  const parts2 = normalized2.split(/\s+/);
  const last1 = parts1[parts1.length - 1];
  const last2 = parts2[parts2.length - 1];
  
  // Check last names first (most reliable identifier)
  if (last1 === last2 && last1.length > 2) {
    // Last names match, now check first names
    // Get first name(s) - everything except last name
    const first1 = parts1.slice(0, -1).join(' ');
    const first2 = parts2.slice(0, -1).join(' ');
    
    // Remove single-letter words (initials) before comparing
    // This handles "theodora m" vs "theodora" -> both become "theodora"
    const cleanFirst1 = first1.replace(/\b[a-z]\b/g, '').trim();
    const cleanFirst2 = first2.replace(/\b[a-z]\b/g, '').trim();
    
    // If cleaned first names match, it's a match
    if (cleanFirst1 === cleanFirst2 && cleanFirst1.length > 0) {
      return true;
    }
    
    // Check if one first name contains the other (handles "theodora" vs "theodora m")
    if (first1.includes(first2) || first2.includes(first1)) {
      return true;
    }
    
    // Check initials match
    const initials1 = extractInitials(name1);
    const initials2 = extractInitials(name2);
    
    if (initials1.length > 0 && initials2.length > 0) {
      // Check if first and last initials match (most important)
      if (initials1[0] === initials2[0] && 
          initials1[initials1.length - 1] === initials2[initials2.length - 1]) {
        return true;
      }
      
      // Check if all initials from the shorter set are in the longer set
      const shorter = initials1.length <= initials2.length ? initials1 : initials2;
      const longer = initials1.length > initials2.length ? initials1 : initials2;
      
      const allMatch = shorter.every(initial => longer.includes(initial));
      if (allMatch) {
        return true;
      }
    }
    
    // If last name matches and one is clearly a shortened version of the other
    if (parts1.length === 2 && parts2.length >= 2) {
      // "T. Scarato" vs "Theodora M. Scarato"
      const first1 = parts1[0];
      const first2 = parts2[0];
      if (first1.length <= 3 && first2.length > 3 && last1 === last2) {
        return true;
      }
    }
  }

  // Fallback: Check if one name contains the other (for other variations)
  if (normalized1.includes(normalized2) || normalized2.includes(normalized1)) {
    return true;
  }

  return false;
}

/**
 * Check if a filer name matches any spam filer in the internal or user list
 */
export async function isSpamFiler(
  filerName: string,
  userId?: string
): Promise<{ isSpam: boolean; reason?: string }> {
  try {
    // Check internal spam filers
    const { data: internalSpam, error: internalError } = await supabase
      .from('spam_filers')
      .select('filer_name, name_variants, reason');

    if (internalError) {
      console.error('Error fetching internal spam filers:', internalError);
    } else if (internalSpam) {
      for (const spamFiler of internalSpam) {
        // Check main name
        if (fuzzyMatchName(filerName, spamFiler.filer_name)) {
          return {
            isSpam: true,
            reason: spamFiler.reason || `Filer matches internal spam list: ${spamFiler.filer_name}`
          };
        }
        
        // Check name variants
        if (spamFiler.name_variants && Array.isArray(spamFiler.name_variants)) {
          for (const variant of spamFiler.name_variants) {
            if (fuzzyMatchName(filerName, variant)) {
              return {
                isSpam: true,
                reason: spamFiler.reason || `Filer matches internal spam list: ${spamFiler.filer_name}`
              };
            }
          }
        }
      }
    }

    // Check user-specific spam filers if userId is provided
    if (userId) {
      const { data: userSpam, error: userError } = await supabase
        .from('user_spam_filers')
        .select('filer_name, name_variants')
        .eq('user_id', userId);

      if (userError) {
        console.error('Error fetching user spam filers:', userError);
      } else if (userSpam) {
        for (const spamFiler of userSpam) {
          // Check main name
          if (fuzzyMatchName(filerName, spamFiler.filer_name)) {
            return {
              isSpam: true,
              reason: `Filer matches your spam list: ${spamFiler.filer_name}`
            };
          }
          
          // Check name variants
          if (spamFiler.name_variants && Array.isArray(spamFiler.name_variants)) {
            for (const variant of spamFiler.name_variants) {
              if (fuzzyMatchName(filerName, variant)) {
                return {
                  isSpam: true,
                  reason: `Filer matches your spam list: ${spamFiler.filer_name}`
                };
              }
            }
          }
        }
      }
    }

    return { isSpam: false };
  } catch (error) {
    console.error('Error checking spam filer:', error);
    // On error, don't block - return false to allow processing
    return { isSpam: false };
  }
}

export interface BatchSpamCheckResult {
  spamNames: Set<string>;
  reasonByName: Map<string, string>;
}

interface SpamRow {
  filer_name: string;
  name_variants?: string[] | null;
  reason?: string | null;
}

function buildCandidateNames(rows: SpamRow[]): Array<{ candidate: string; reason: string }> {
  const candidates: Array<{ candidate: string; reason: string }> = [];
  for (const row of rows) {
    if (!row?.filer_name) continue;
    const baseReason = row.reason || `Filer matches spam list: ${row.filer_name}`;
    candidates.push({ candidate: row.filer_name, reason: baseReason });
    if (Array.isArray(row.name_variants)) {
      for (const variant of row.name_variants) {
        if (typeof variant === 'string' && variant.trim().length > 0) {
          candidates.push({ candidate: variant, reason: baseReason });
        }
      }
    }
  }
  return candidates;
}

export async function batchCheckSpamFilers(
  filerNames: string[],
  userId?: string
): Promise<BatchSpamCheckResult> {
  const uniqueNames = Array.from(new Set(filerNames.map(name => name?.trim()).filter(Boolean) as string[]));
  if (uniqueNames.length === 0) {
    return { spamNames: new Set(), reasonByName: new Map() };
  }

  if (uniqueNames.length >= 25) {
    try {
      // Best-performance path for larger dockets: resolve in one backend call.
      const serverResult = await functionsFetch<{ spamMatches?: Array<{ filerName: string; reason?: string }> }>('/check-spam-filers-batch', {
        method: 'POST',
        body: JSON.stringify({ filerNames: uniqueNames }),
      });
      const spamNames = new Set<string>();
      const reasonByName = new Map<string, string>();
      for (const match of serverResult.spamMatches || []) {
        if (!match?.filerName) continue;
        const key = match.filerName.trim();
        spamNames.add(key);
        if (match.reason) reasonByName.set(key, match.reason);
      }
      return { spamNames, reasonByName };
    } catch {
      // Fallback below.
    }
  }

  try {
    const [{ data: internalSpam, error: internalError }, userSpamResult] = await Promise.all([
      supabase
        .from('spam_filers')
        .select('filer_name, name_variants, reason'),
      userId
        ? supabase
            .from('user_spam_filers')
            .select('filer_name, name_variants')
            .eq('user_id', userId)
        : Promise.resolve({ data: [], error: null } as { data: SpamRow[]; error: any }),
    ]);

    if (internalError) {
      console.error('Error fetching internal spam filers:', internalError);
    }
    if (userId && userSpamResult.error) {
      console.error('Error fetching user spam filers:', userSpamResult.error);
    }

    const internalRows: SpamRow[] = (internalSpam || []) as SpamRow[];
    const userRows: SpamRow[] = ((userSpamResult.data || []) as SpamRow[]).map(row => ({
      ...row,
      reason: `Filer matches your spam list: ${row.filer_name}`,
    }));
    const candidates = buildCandidateNames([...internalRows, ...userRows]);

    const spamNames = new Set<string>();
    const reasonByName = new Map<string, string>();

    for (const filerName of uniqueNames) {
      for (const entry of candidates) {
        if (fuzzyMatchName(filerName, entry.candidate)) {
          spamNames.add(filerName);
          reasonByName.set(filerName, entry.reason);
          break;
        }
      }
    }

    return { spamNames, reasonByName };
  } catch (error) {
    console.error('Error batch checking spam filers:', error);
    return { spamNames: new Set(), reasonByName: new Map() };
  }
}

/**
 * Get user's spam filer list
 */
export async function getUserSpamFilers(userId: string): Promise<string[]> {
  try {
    const { data, error } = await supabase
      .from('user_spam_filers')
      .select('filer_name')
      .eq('user_id', userId)
      .order('filer_name', { ascending: true });

    if (error) {
      console.error('Error fetching user spam filers:', error);
      return [];
    }

    return (data || []).map(item => item.filer_name);
  } catch (error) {
    console.error('Error getting user spam filers:', error);
    return [];
  }
}

/**
 * Add a filer to user's spam list
 */
export async function addUserSpamFiler(
  userId: string,
  filerName: string,
  nameVariants?: string[]
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('user_spam_filers')
      .insert({
        user_id: userId,
        filer_name: filerName.trim(),
        name_variants: nameVariants || []
      });

    if (error) {
      // If it's a unique constraint violation, that's okay - already exists
      if (error.code === '23505') {
        return true;
      }
      console.error('Error adding user spam filer:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error adding user spam filer:', error);
    return false;
  }
}

/**
 * Remove a filer from user's spam list
 */
export async function removeUserSpamFiler(
  userId: string,
  filerName: string
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('user_spam_filers')
      .delete()
      .eq('user_id', userId)
      .eq('filer_name', filerName.trim());

    if (error) {
      console.error('Error removing user spam filer:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error removing user spam filer:', error);
    return false;
  }
}

/**
 * Check if a filing should be considered spam based on attachment count
 * Returns true if filing has 5+ attachments and is NOT an NPRM
 */
export function isSpamByAttachmentCount(
  attachmentCount: number,
  filingType: string
): boolean {
  // NPRM filings can have many attachments legitimately
  const isNPRM = filingType === 'NPRM' || 
                 filingType?.toUpperCase() === 'NPRM' ||
                 filingType?.toLowerCase() === 'nprm';
  
  if (isNPRM) {
    return false;
  }

  // 5 or more attachments is considered spam
  return attachmentCount >= 5;
}

